/**
 * Attendance (fingerprint) — shared helpers for the /attendance screens.
 * Backend: backend/routes/attendance.py (spec ATTENDANCE_FEATURE_SPEC.md §4).
 */
import axios from 'axios';

export const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/attendance';

export const api = (token) => {
  const headers = { Authorization: `Bearer ${token}` };
  return {
    get: (path, params) => axios.get(`${API_URL}${path}`, { headers, params }).then((r) => r.data),
    post: (path, body, params) => axios.post(`${API_URL}${path}`, body || {}, { headers, params }).then((r) => r.data),
    put: (path, body) => axios.put(`${API_URL}${path}`, body, { headers }).then((r) => r.data),
    patch: (path, body) => axios.patch(`${API_URL}${path}`, body, { headers }).then((r) => r.data),
    del: (path, params) => axios.delete(`${API_URL}${path}`, { headers, params }).then((r) => r.data),
  };
};

export const errorText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d[0]?.msg || e.message;
  if (d && typeof d === 'object') return d.error || (Array.isArray(d.detail) ? d.detail[0]?.msg : null) || e.message;
  return d || e?.message || 'Error';
};

export const HR_SCOPE_ROLES = ['super_admin', 'hr_manager', 'hr_admin'];
export const MANAGE_ROLES = ['super_admin', 'hr_manager', 'hr_admin'];
export const RELEASE_ROLES = ['super_admin', 'hr_manager'];

export const thisMonth = () => new Date().toISOString().slice(0, 7);
export const today = () => {
  // Riyadh date (UTC+3), the devices' clock
  const d = new Date(Date.now() + 3 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
};

export const hm = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === '') return '—';
  const m = Math.round(Number(minutes));
  const sign = m < 0 ? '-' : '';
  const a = Math.abs(m);
  return `${sign}${Math.floor(a / 60)}:${String(a % 60).padStart(2, '0')}`;
};

export const clock = (iso) => (iso ? String(iso).slice(11, 16) : '—');

// Day statuses (D5: statuses replace the old colours)
export const STATUS = {
  present: { en: 'Present', ar: 'حاضر', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  in_progress: { en: 'In progress', ar: 'جارٍ', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  future: { en: '—', ar: '—', cls: 'bg-white text-slate-300 border-slate-100' },
  off: { en: 'Off day', ar: 'راحة', cls: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  holiday: { en: 'Holiday', ar: 'عطلة', cls: 'bg-green-50 text-green-800 border-green-200' },
  on_leave: { en: 'On leave', ar: 'إجازة', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  paid_day_off: { en: 'Incentive day off', ar: 'يوم راحة تحفيزي', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  absent_unexcused: { en: 'Absent (unexcused)', ar: 'غياب بدون عذر', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  absent_excused: { en: 'Absent (excused)', ar: 'غياب بعذر', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  half_day_leave: { en: 'Half day (leave)', ar: 'نصف يوم (إجازة)', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  half_day_unexcused: { en: 'Half day (unexcused)', ar: 'نصف يوم (بدون عذر)', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  needs_review: { en: 'Needs review', ar: 'يحتاج مراجعة', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  not_employed: { en: 'Not employed', ar: 'خارج فترة العمل', cls: 'bg-slate-50 text-slate-400 border-slate-200' },
};

export const SUGGESTION = {
  missing_punch: { en: 'Missing punch', ar: 'بصمة ناقصة' },
  single_shift: { en: 'Only one shift', ar: 'فترة واحدة فقط' },
  long_span: { en: 'Long span, no break punched', ar: 'فترة طويلة بلا بصمة استراحة' },
  absence: { en: 'No punches, nothing on file', ar: 'لا بصمات ولا شيء مسجل' },
  leave_pending: { en: 'Leave awaiting approval', ar: 'إجازة بانتظار الاعتماد' },
  punch_during_leave: { en: 'Punching during leave', ar: 'بصمة أثناء إجازة' },
  leave_applied: { en: 'Leave applied', ar: 'تم تطبيق الإجازة' },
  half_day_leave_applied: { en: 'Half-day leave applied', ar: 'تم تطبيق نصف يوم إجازة' },
  off_day_work: { en: 'Worked on an off day', ar: 'عمل في يوم راحة' },
  holiday_work: { en: 'Worked on a holiday', ar: 'عمل في عطلة رسمية' },
  late_arrival: { en: 'Late arrival', ar: 'تأخر في الحضور' },
  early_leave: { en: 'Early leave', ar: 'انصراف مبكر' },
  overtime_not_preapproved: { en: 'Overtime not pre-approved', ar: 'إضافي غير معتمد مسبقاً' },
  terminated_punching: { en: 'Former employee still punching', ar: 'موظف سابق ما زال يبصم' },
};

export const ACTION = {
  add_exit_at_usual: { en: 'Add exit at usual time', ar: 'إضافة خروج بالوقت المعتاد' },
  add_entry_at_usual: { en: 'Add entry at usual time', ar: 'إضافة دخول بالوقت المعتاد' },
  half_day_leave: { en: 'Half-day leave', ar: 'نصف يوم إجازة' },
  half_day_unexcused: { en: 'Half-day unexcused absence', ar: 'نصف يوم غياب بدون عذر' },
  ask_employee: { en: 'Ask employee', ar: 'سؤال الموظف' },
  ask_manager: { en: 'Ask manager', ar: 'سؤال المدير' },
  worked_forgot_punch: { en: 'Worked, forgot to punch', ar: 'عمل ونسي البصمة' },
  split_at_usual_break: { en: 'Split at usual break', ar: 'تقسيم عند الاستراحة المعتادة' },
  keep_as_worked: { en: 'Keep as worked', ar: 'احتسابه كما هو' },
  unexcused_absence: { en: 'Unexcused absence', ar: 'غياب بدون عذر' },
  approve_leave: { en: 'Approve the leave (Leave screen)', ar: 'اعتماد الإجازة (شاشة الإجازات)' },
  reject_mark_absent: { en: 'Reject, mark absent', ar: 'رفض وتسجيل غياب' },
  shorten_leave: { en: 'Shorten the leave (Leave screen)', ar: 'تقصير الإجازة (شاشة الإجازات)' },
  ignore_punches: { en: 'Ignore the punches', ar: 'تجاهل البصمات' },
  confirm_overtime: { en: 'Confirm as overtime', ar: 'اعتماد كإضافي' },
  accept: { en: 'Accept', ar: 'قبول' },
  deduct: { en: 'Deduct', ar: 'خصم' },
  excuse: { en: 'Excuse', ar: 'عذر' },
  approve_overtime: { en: 'Approve overtime', ar: 'اعتماد الإضافي' },
  not_overtime: { en: 'Not overtime', ar: 'ليس إضافياً' },
  investigate: { en: 'Investigate', ar: 'تحقق' },
  inform_branch_manager: { en: 'Inform branch manager', ar: 'إبلاغ مدير الفرع' },
};

export const REASONS = [
  { en: 'Confirmed with the employee', ar: 'تم التأكد مع الموظف' },
  { en: 'Confirmed with the direct manager', ar: 'تم التأكد مع المدير المباشر' },
  { en: 'Approved request', ar: 'طلب معتمد' },
  { en: 'Device or network problem', ar: 'مشكلة في الجهاز أو الشبكة' },
  { en: 'HR decision', ar: 'قرار الموارد البشرية' },
];

export const TS_STATUS = {
  draft: { en: 'Draft', ar: 'مسودة', cls: 'bg-slate-100 text-slate-700' },
  returned: { en: 'Returned by the manager', ar: 'أعاده المدير المباشر', cls: 'bg-rose-100 text-rose-800' },
  manager_approved: { en: 'Manager approved', ar: 'اعتمده المدير المباشر', cls: 'bg-violet-100 text-violet-800' },
  hr_approved: { en: 'HR approved', ar: 'معتمد من الموارد البشرية', cls: 'bg-sky-100 text-sky-800' },
  employee_confirmed: { en: 'Employee confirmed', ar: 'أكده الموظف', cls: 'bg-emerald-100 text-emerald-800' },
  disputed: { en: 'Disputed', ar: 'معترض عليه', cls: 'bg-amber-100 text-amber-800' },
  final: { en: 'Final', ar: 'نهائي', cls: 'bg-slate-800 text-white' },
  released: { en: 'Released', ar: 'تم الإطلاق', cls: 'bg-brand-orange text-white' },
};

export const label = (map, key, isRTL) => {
  const v = map[key];
  return v ? (isRTL ? v.ar : v.en) : key;
};

export const employeeName = (emp, isRTL) => (emp ? (isRTL ? emp.name_ar || emp.name : emp.name || emp.name_ar) : '');
